import * as v from "valibot";
import type { Schema } from "@aqora-io/aqora-data-utils";
import {
  get_simple_schema_field,
  SimpleSchemaFieldKind,
  ScalarType,
  SimpleSchemaScalar,
  SimpleSchemaList,
  SimpleSchemaStruct,
  SimpleSchemaUnknown,
  SimpleSchemaField,
  SimpleSchemaFieldBase,
} from "@/utils/data_utils_schema";

const editFieldSchema: v.GenericSchema<SimpleSchemaField> = v.variant("kind", [
  v.object({
    kind: v.literal(SimpleSchemaFieldKind.Scalar),
    name: v.string(),
    nullable: v.boolean(),
    scalar: v.enum(ScalarType),
  }),
  v.object({
    kind: v.literal(SimpleSchemaFieldKind.List),
    name: v.string(),
    nullable: v.boolean(),
    field: v.lazy(() => editFieldSchema),
  }),
  v.object({
    kind: v.literal(SimpleSchemaFieldKind.Struct),
    name: v.string(),
    nullable: v.boolean(),
    fields: v.array(v.lazy(() => editFieldSchema)),
  }),
  v.object({
    kind: v.literal(SimpleSchemaFieldKind.Unsupported),
    name: v.string(),
    nullable: v.boolean(),
  }),
]);

export const editSchemaSchema = v.object({
  fields: v.array(editFieldSchema),
});

type FormEditScalar<P extends string> = SimpleSchemaScalar & {
  prefix: P;
};

type FormEditList<P extends string> = Omit<SimpleSchemaList, "field"> & {
  prefix: P;
  field: FormEditField<`${P}.field`>;
};

type FormEditStruct<P extends string> = Omit<SimpleSchemaStruct, "fields"> & {
  prefix: P;
  fields: FormEditField<`${P}.fields.${number}`>[];
};

type FormEditUnknown<P extends string> = SimpleSchemaUnknown & { prefix: P };

export type FormEditField<P extends string = string> = SimpleSchemaFieldBase &
  (
    | FormEditScalar<P>
    | FormEditList<P>
    | FormEditStruct<P>
    | FormEditUnknown<P>
  );

const apply_prefix = <P extends string>(
  field: SimpleSchemaField,
  prefix: P,
): FormEditField<P> => {
  switch (field.kind) {
    case SimpleSchemaFieldKind.Scalar:
    case SimpleSchemaFieldKind.Unsupported:
      return { ...field, prefix };
    case SimpleSchemaFieldKind.List:
      return {
        ...field,
        prefix,
        field: apply_prefix(field.field, `${prefix}.field`),
      };
    case SimpleSchemaFieldKind.Struct:
      return {
        ...field,
        prefix,
        fields: field.fields.map((field, index) =>
          apply_prefix(field, `${prefix}.fields.${index}`),
        ),
      };
  }
};

export type SchemaFieldPrefix = `fields.${number}`;

export type FormEditSchema = {
  fields: FormEditField<SchemaFieldPrefix>[];
};

export const get_edit_schema = (schema: Schema): FormEditSchema => {
  return {
    fields: schema.fields.map((field, index) =>
      apply_prefix(get_simple_schema_field(field), `fields.${index}`),
    ),
  };
};

const edit_field_children = <P extends string>(
  field: FormEditField<P>,
): FormEditField<P>[] => {
  switch (field.kind) {
    case SimpleSchemaFieldKind.List:
      return [field.field] as FormEditField<P>[];
    case SimpleSchemaFieldKind.Struct:
      return field.fields as FormEditField<P>[];
    case SimpleSchemaFieldKind.Scalar:
    case SimpleSchemaFieldKind.Unsupported:
      return [];
  }
};

const focus_fields = <F extends { name: string }>(
  fields: F[],
  path: string[],
  get_children: (field: F) => F[],
): F[] => {
  for (const p of path) {
    const field = fields.find((f) => f.name === p);
    fields = field ? get_children(field) : [];
  }
  return fields;
};

export const focus_edit_fields = (
  edit_schema: FormEditSchema,
  path: string[],
) => focus_fields(edit_schema.fields, path, edit_field_children);
